/**
 * Authentication Context
 * Manages user authentication state and Keycloak integration
 */
import * as React from 'react'
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web'
import type { User } from '@/types/user.types'
import { keycloak, keycloakInitOptions } from '@/lib/keycloak'
import { redirectToLogin } from '@/lib/auth-redirect'
import { useCurrentUser } from '@/hooks/repositories/useUsersRepository'
import { useSyncGooglePicture } from '@/hooks/repositories/useAuthRepository'

const BASE_PATH = import.meta.env.VITE_BASE_PATH || ''

/**
 * sessionStorage key marking that the Google picture sync already ran for this
 * browser session, so a page refresh does not re-upload the picture.
 */
const PICTURE_SYNCED_KEY = 'picture-synced'

function clearPictureSyncedFlag() {
  try {
    sessionStorage.removeItem(PICTURE_SYNCED_KEY)
  } catch {
    // no-op (storage unavailable)
  }
}

/** Stable identity so an absent permission set does not re-render consumers. */
const NO_PERMISSIONS: ReadonlyArray<string> = []

export type AuthContextType = {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  /**
   * True once the user's `department` has actually been resolved from the
   * backend (not just Keycloak init). `department` is populated by an async
   * fetch *after* `isLoading` flips false, so route guards must wait on this —
   * not `isLoading` — before making a department-based redirect decision, or
   * they bounce admins on a cold refresh/deep-link. Undefined department means
   * "still loading or the fetch errored"; guards stay put (fail-open) and rely
   * on the server-side @RequireAdmin guard as the real boundary.
   */
  departmentResolved: boolean
  /**
   * Permission keys the signed-in user effectively holds. An ADMIN comes back
   * holding the whole catalog, so this can be checked directly without also
   * testing the department (sc-1215).
   *
   * Kept alongside `user` rather than on it: permissions land as soon as the
   * /me fetch resolves, whereas `user.department` additionally waits on
   * Keycloak's userinfo round trip, and there is no reason to make the faster
   * one wait for the slower.
   */
  permissions: ReadonlyArray<string>
  /**
   * True once the permission set has actually been fetched. Same fail-open
   * contract as `departmentResolved`: while false, route guards stay put and
   * the server-side permission guard is the real boundary — bouncing early
   * would kick legitimate users off a cold refresh or deep link.
   */
  permissionsResolved: boolean
  token: string | null
  login: (idpHint?: string, redirectUri?: string) => void
  loginWithProvider: (providerId: string, redirectUri?: string) => void
  logout: () => void
  refreshToken: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const { keycloak: kc, initialized } = useKeycloak()
  const [user, setUser] = React.useState<User | null>(null)
  const [keycloakId, setKeycloakId] = React.useState<string | null>(null)

  // Fetch user data from our backend by Keycloak ID
  const { data: backendUserData } = useCurrentUser({ enabled: !!keycloakId })

  // Post-login Google picture sync (sc-1227). Runs once per browser session:
  // the ref guards against the userinfo effect re-running on every
  // `backendUserData` change, sessionStorage guards against page refreshes.
  const { mutate: syncGooglePicture } = useSyncGooglePicture()
  const pictureSyncedRef = React.useRef(false)
  const syncGooglePictureOnce = React.useCallback(
    (keycloakSub: string, pictureUrl: string | undefined) => {
      if (!pictureUrl || pictureSyncedRef.current) return
      let alreadySynced = false
      try {
        alreadySynced =
          sessionStorage.getItem(PICTURE_SYNCED_KEY) === keycloakSub
      } catch {
        // storage unavailable: fall back to the in-memory guard only
      }
      // Set the in-memory guard up front so concurrent effect runs don't fire twice,
      // but only persist the session flag once the request actually succeeded so an
      // HTTP-level failure (network, 401, DTO validation) can be retried later.
      pictureSyncedRef.current = true
      if (alreadySynced) return
      syncGooglePicture(
        { pictureUrl },
        {
          onSuccess: () => {
            try {
              sessionStorage.setItem(PICTURE_SYNCED_KEY, keycloakSub)
            } catch {
              // no-op
            }
          },
          onError: () => {
            pictureSyncedRef.current = false
          },
        },
      )
    },
    [syncGooglePicture],
  )

  const permissions = React.useMemo(
    () => backendUserData?.permissions ?? NO_PERMISSIONS,
    [backendUserData?.permissions],
  )

  // Extract Keycloak ID from token when authenticated
  React.useEffect(() => {
    if (initialized && kc.authenticated && kc.tokenParsed) {
      const tokenParsed = kc.tokenParsed as {
        sub: string
        preferred_username: string
        email: string
        given_name?: string
        family_name?: string
        realm_access?: { roles: Array<string> }
      }

      // Store the Keycloak ID to trigger the backend user data fetch
      setKeycloakId(tokenParsed.sub)
    } else if (initialized && !kc.authenticated) {
      setKeycloakId(null)
    }
  }, [initialized, kc.authenticated, kc.tokenParsed])

  // Extract user info from Keycloak token and userinfo endpoint
  // Combine with backend user data when available
  React.useEffect(() => {
    if (initialized && kc.authenticated && kc.tokenParsed) {
      const tokenParsed = kc.tokenParsed as {
        sub: string
        preferred_username: string
        email: string
        given_name?: string
        family_name?: string
        realm_access?: { roles: Array<string> }
      }

      // Load additional user info from userinfo endpoint (includes custom attributes)
      kc.loadUserInfo()
        .then((userInfo) => {
          const userInfoData = userInfo as {
            picture?: string
            [key: string]: unknown
          }

          const userData: User = {
            id: tokenParsed.sub,
            username: tokenParsed.preferred_username,
            email: tokenParsed.email,
            firstName: tokenParsed.given_name ?? '',
            lastName: tokenParsed.family_name ?? '',
            picture: userInfoData.picture,
            databaseId: backendUserData?.id, // Add database ID from backend
            department: backendUserData?.department, // Add department from backend
          }

          setUser(userData)
          syncGooglePictureOnce(tokenParsed.sub, userInfoData.picture)
        })
        .catch((error) => {
          console.error('Failed to load user info:', error)
          // Still set basic user data from token if userinfo fails
          const userData: User = {
            id: tokenParsed.sub,
            username: tokenParsed.preferred_username,
            email: tokenParsed.email,
            firstName: tokenParsed.given_name ?? '',
            lastName: tokenParsed.family_name ?? '',
            databaseId: backendUserData?.id, // Add database ID from backend
            department: backendUserData?.department, // Add department from backend
          }
          setUser(userData)
        })
    } else if (initialized && !kc.authenticated) {
      setUser(null)
    }
  }, [
    initialized,
    kc.authenticated,
    kc.tokenParsed,
    backendUserData,
    syncGooglePictureOnce,
  ])

  // Auto-refresh token
  React.useEffect(() => {
    if (initialized && kc.authenticated) {
      // Update token every 30 seconds
      const interval = setInterval(() => {
        kc.updateToken(70)
          .then((refreshed) => {
            if (refreshed) {
              console.info('Keycloak: Token refreshed')
            }
          })
          .catch(() => {
            console.error('Keycloak: Failed to refresh token')
            // Clear user state
            setUser(null)
            setKeycloakId(null)
            pictureSyncedRef.current = false
            clearPictureSyncedFlag()
            // Session is dead — clear local token and hard-redirect to /login.
            // We avoid kc.logout() because Keycloak's logout endpoint is
            // unreliable when the server-side session no longer exists.
            try {
              kc.clearToken()
            } catch {
              // no-op
            }
            redirectToLogin()
          })
      }, 30000)

      return () => clearInterval(interval)
    }
  }, [initialized, kc])

  const login = React.useCallback(
    (idpHint?: string, redirectUri?: string) => {
      kc.login({
        redirectUri: redirectUri ?? window.location.origin + BASE_PATH + '/',
        idpHint,
      })
    },
    [kc],
  )

  const loginWithProvider = React.useCallback(
    (providerId: string, redirectUri?: string) => {
      kc.login({
        redirectUri: redirectUri ?? window.location.origin + BASE_PATH + '/',
        idpHint: providerId,
      })
    },
    [kc],
  )

  const logout = React.useCallback(() => {
    // Clear user state before logout
    setUser(null)
    setKeycloakId(null)
    pictureSyncedRef.current = false
    clearPictureSyncedFlag()
    // Logout with safe redirect
    kc.logout({
      redirectUri: window.location.origin + BASE_PATH,
    }).catch((error) => {
      console.error('Keycloak: Logout failed', error)
      // Force redirect to root if logout fails
      window.location.href = BASE_PATH || '/'
    })
  }, [kc])

  const refreshToken = React.useCallback(async () => {
    try {
      await kc.updateToken(5)
    } catch (error) {
      console.error('Keycloak: Failed to refresh token:', error)
      setUser(null)
      setKeycloakId(null)
      try {
        kc.clearToken()
      } catch {
        // no-op
      }
      redirectToLogin()
    }
  }, [kc])

  const value = React.useMemo(
    () => ({
      user,
      isAuthenticated: !!kc.authenticated,
      isLoading: !initialized,
      // Resolved once the backend fetch has populated a department on the user.
      departmentResolved: !!user && user.department !== undefined,
      permissions,
      permissionsResolved: !!backendUserData?.permissions,
      token: kc.token || null,
      login,
      loginWithProvider,
      logout,
      refreshToken,
    }),
    [
      user,
      permissions,
      backendUserData?.permissions,
      kc.authenticated,
      initialized,
      kc.token,
      login,
      loginWithProvider,
      logout,
      refreshToken,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [initError, setInitError] = React.useState<string | null>(null)

  const handleKeycloakEvent = (_: unknown, error: unknown) => {
    if (error) {
      console.error('Keycloak: Error:', error)
      setInitError(String(error))
    }
  }

  const handleKeycloakTokens = (/* tokens: unknown */) => {}

  if (initError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md rounded-lg border border-destructive bg-destructive/10 p-6">
          <h2 className="mb-2 text-lg font-semibold text-destructive">
            Authentication Error
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">{initError}</p>
          <p className="text-xs text-muted-foreground">
            Please check if Keycloak is running at{' '}
            {import.meta.env.VITE_KEYCLOAK_DOMAIN}
          </p>
        </div>
      </div>
    )
  }

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={keycloakInitOptions}
      onEvent={handleKeycloakEvent}
      onTokens={handleKeycloakTokens}
      LoadingComponent={
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">
              Initializing authentication...
            </p>
          </div>
        </div>
      }
    >
      <AuthProviderInner>{children}</AuthProviderInner>
    </ReactKeycloakProvider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
